import type { AcceptTermsRequest, AcceptTermsResponse, TermsAcceptSource } from '@lunya/contracts'
import { API_PATHS } from '@lunya/contracts'

import { createMutation } from '~/shared/api'

import { mapUserFromApi } from './mappers/mapUserFromApi'
import { UserQuery } from './UserQuery'

export type AcceptTermsInput = {
  /** `onboarding` — строка под кнопкой первого экрана, `resume` — шторка при входе. */
  source: TermsAcceptSource
}

/**
 * POST /users/me/terms — зафиксировать акцепт оферты.
 *
 * Редакции в запросе нет: её ставит сервер из `TERMS_VERSION`. Клиент, который сам сообщал бы,
 * какой документ принят, записал бы акцепт текста, которого человек не открывал, — а именно
 * эта запись решает спор о том, на каких условиях заключён договор (docs/legal.md §13.3).
 *
 * `idempotency: true` — повтор при плохой сети безопасен: уникальный индекс `(user_id, version)`
 * на сервере не даёт ни второй записи, ни сдвига момента акцепта.
 */
export const AcceptTermsMutation = createMutation<
  AcceptTermsInput,
  AcceptTermsResponse,
  ReturnType<typeof mapUserFromApi>
>({
  key: 'user.acceptTerms',
  path: API_PATHS.user.terms,
  method: 'POST',
  getBody: (input): AcceptTermsRequest => ({ source: input.source }),
  transform: mapUserFromApi,
  idempotency: true,
  sideEffects: {
    // Ответ — тот же профиль, что отдаёт `GET /users/me`: кладём его в кэш напрямую вместо
    // инвалидации. Иначе шторка акцепта, которая смотрит в тот же кэш, успела бы моргнуть
    // ещё раз, пока едет повторный запрос профиля.
    onSuccess: (user, { queryClient }) => {
      queryClient.setQueryData(UserQuery.getQueryKey(), user)
    },
  },
})
